import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Tab } from 'semantic-ui-react'
import i18n from '~/components/i18n'
import { useDebounce } from 'use-lodash-debounce'
import notify from '~/components/Notify'
import { useNavigate, useParams } from 'react-router-dom';
import { RouteTypes } from '~/routes/constants'
import { UsersQuery } from '~/QueryHooks/UsersQuery'
import TableComponent from '~/components/TableComponent'
import Dialog from '~/components/Dialog'
import Layout from '~/components/Layout'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import UsersService from '~/api/services/users'
import { FarmersQuery } from '~/QueryHooks/Farmers'

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Email'),
    key: 'email'
  },
  {
    name: i18n.t('Mobile number'),
    key: 'mobileNumber',
    type: 'mobileNumber'
  },
  {
    name: i18n.t('Verified'),
    key: 'verified',
    type: 'verified'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'status'
  },
  {
    name: i18n.t('Last login'),
    key: 'lastLogin',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Actions')
  }
]

const defaultFilter = {
  page: 0,
  type: 'all_farmer_users',
  search: '',
  status: null,
  rows: 10
}

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit'
  },
  {
    popupMessage: i18n.t('Unblock'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'blockUnblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Send password reset link'),
    type: 'icon',
    name: 'mail',
    size: 'large',
    actionType: 'mail',
    verifiedAction: 1,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Resend verification email'),
    type: 'icon',
    name: 'linkify',
    size: 'large',
    actionType: 'resend',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Block'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'blockUnblock',
    display: [1]
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  }
]

const getActionsForLightUser = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.USERS_SHOW
  }
]

const Users: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [dataFiltered, setDataFiltered] = useState({ farmerId: id, ...defaultFilter })
  const [loadingModalAction, setLoadingModalAction] = useState(false)
  const [blockFarmerUser, setBlockFarmerUser] = useState(null)
  const [deleteUserData, setDeleteUserData] = useState(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const navigate = useNavigate()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [search, setSearch] = useState('')
  const debouncedValue = useDebounce(search, 300)

  const {
    data: farmerUsersData,
    isLoading: isBusyUsers,
    refetch: fetchFilteredData,
    isFetching: isFetching
  } = FarmersQuery.getShowFarmerUsersData({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: farmerOptions,
    isLoading: isBusy,
    refetch: fetchOptions
  } = FarmersQuery.getShowFarmerUsersOptions()

  useEffect(() => {
    fetchOptions()
  }, [t])

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered])

  const onBlockUserSuccess = () => {
    notify(t('User successfully blocked'), 'success');
    fetchFilteredData();
  };

  const onUnblockUserSuccess = () => {
    notify(t('User successfully unblocked'), 'success');
    fetchFilteredData();
  };

  const onSendPasswordResetLinkSuccess = () => {
    notify(t('Password reset link successfully sent to user'), 'success');
  };

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  const { isLoading: isBlockUserLoading, mutate: blockUserById } = UsersQuery.blockUserById(
    onBlockUserSuccess
  );

  const { isLoading: isUnblockUserLoading, mutate: unblockUserById } = UsersQuery.unblockUserById(
    onUnblockUserSuccess
  );

  const { isLoading: isSendPasswordResetLinkLoading, mutate: sendPasswordResetLinkToUserById } = UsersQuery.sendPasswordResetLinkToUserById(
    onSendPasswordResetLinkSuccess
  );

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  async function resendPaswordResetLink(id) {
    try {
      await UsersService.resendActivation(id)
      notify(t('Successfully sent reset link'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'blockUnblock':
        return setBlockFarmerUser(row)
      case 'mail':
        return sendPasswordResetLinkToUserById(row.id)
      case 'edit':
        return navigate(RouteTypes.FARMER_USERS_EDIT.replace(':farmerId', row.farmerId).replace(':id', row.id))
      case 'resend':
        return resendPaswordResetLink(row.id)
      case 'delete':
        return setDeleteUserData(row)
      default:
        return
    }
  }

  function renderContent() {
    return <TableComponent
      actions={lightUserLogged ? getActionsForLightUser() : getActions()}
      tableColumnHeaders={getTableColumnHeaders()}
      animated
      isFetchingData={isFetching}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    const status = farmerOptions?.types?.find((_, indexOption) => indexOption === activeIndex)
    if (!isFetching) {
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        type: status.key,
        page: 0
      })
    }
  }

  function renderPanes() {
    const panes = farmerOptions?.types?.map(option => {
      return {
        menuItem: option.label,
        render: () => renderContent()
      }
    })

    return <Tab
      menu={{ secondary: true, pointing: true }}
      panes={panes}
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => handleTabChange(data.activeIndex)}
    />
  }

  const handleBlockUser = (farmer) => {
    if (farmer.status === 1) {
      blockUserById(farmer.id);
    } else if (farmer.status === 0) {
      unblockUserById(farmer.id);
    }
    setBlockFarmerUser(null)
  };

  function renderBlockModal() {
    const action = blockFarmerUser.status === 1 ? t('Block') : t('Unblock')
    const name = blockFarmerUser?.name
    return <Dialog
      isDialogOpen={Boolean(blockFarmerUser)}
      textClose={t('Close')}
      handleClose={() => setBlockFarmerUser(null)}
      textSubmit={t(`${blockFarmerUser.status === 1 ? 'Block' : 'Unblock'}`)}
      handleSubmit={() => handleBlockUser(blockFarmerUser)}
      textContent={t('Do you want to {{action}} {{name}}?', { action, name })}
      textHeader={t('{{action}} {{name}}', { action, name })}
      type={'abort'}
    />
  }

  async function handleDeleteUser() {
    setLoadingModalAction(true)
    try {
      await UsersService.deleteUser(deleteUserData?.id)
      fetchFilteredData()
      notify(t('Successfully deleted farmer user'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadingModalAction(false)
  }

  function renderDeleteModal() {
    const name = deleteUserData?.name || ''
    return <Dialog
      isDialogOpen={Boolean(deleteUserData)}
      isLoading={loadingModalAction}
      disableSubmit={loadingModalAction}
      textClose={t('Close')}
      handleClose={() => setDeleteUserData(null)}
      textContent={t('Do you want to delete {{name}}?', { name })}
      type={'abort'}
      textSubmit={t('Delete')}
      textHeader={t('Delete farmer user {{name}}', { name })}
      handleSubmit={() => handleDeleteUser()}
    />
  }

  const { filtered, rows } = farmerUsersData ? farmerUsersData : { filtered: 0, rows: [] }
  const loading = isBlockUserLoading || isSendPasswordResetLinkLoading || isUnblockUserLoading || isBusy || isBusyUsers

  return <Layout
    title={t('Farmer User')}
    filters={renderSearch()}
    childContainerSize={'auto'}
    useSpinner
    actionName={t('Add')}
    action={!lightUserLogged && RouteTypes.DAIRY_FACTORY_FARMER_USERS_CREATE.replace(':id', id)}
    isBusy={loading}>
    {renderPanes()}
    {Boolean(deleteUserData) && renderDeleteModal()}
    {Boolean(blockFarmerUser) && renderBlockModal()}
  </Layout>
}
export default Users
